import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  forwardRef,
} from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { AnimatedCheckmark } from 'components/Icon/AnimatedCheckmark';
import { BodySmall } from 'components/Text/BodySmall';
import { colors, transition } from 'theme/theme';

const Prefix = styled(Box)`
  cursor: pointer;
`;

type Props = {
  name?: string;
  type?: string;
  label?: string;
  autoFocus?: boolean;
  changeHandler?: ChangeEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  onKeyPress?: KeyboardEventHandler;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateValue?: (...args: Array<any>) => any;
  className?: string;
  placeholder?: string;
  id?: string;
  hasError?: boolean;
  hasSuccess?: boolean;
  isValid?: boolean;
  prefix?: string;
  value?: string | number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  min?: number | string;
  max?: number | string;
  'data-qa-id'?: string;
  disabled?: boolean;
};

/**
 * @deprecated This input should no longer be used
 *
 * Reason:
 * 1. It has two completely different implementations based on `prefix` being provided.
 * 2. It can include a `successMessage` that is not wrapped in a container.
 * 3. Other components such as `SearchInput` mutate the internal styles, making it difficult to make changes.
 * 4. It accepts too many mutating properties such as color and padding
 * 5. It's not easily extensible - it works well for what's included, but it's hard to, for example, add a new style of prefix
 *
 * Due to this component being used in multiple places it was renamed and not
 * removed entirely, if you're looking for an input please refer to `Input`,
 * it provides the same functionality with a different implementation.
 */
export const LegacyInput = styled(
  forwardRef((props: Props, ref) => {
    const {
      changeHandler,
      onChange: onChangeProp,
      onUpdateValue,
      className,
      id,
      hasError,
      hasSuccess,
      isValid,
      prefix,
      ...otherProps
    } = props;

    const onChange = changeHandler
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e: any) => {
          changeHandler(e.target.value);
        }
      : onChangeProp;

    if (prefix) {
      return (
        <Box
          className={className}
          position="relative"
          display="flex"
          flexDirection="row-reverse"
        >
          <input
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={ref as any}
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...otherProps}
            onChange={onChange}
            id={id}
          />
          <Prefix
            component="label"
            // @ts-expect-error fix me
            htmlFor={id}
            flex="0 0 auto"
            px="8px"
            display="flex"
            alignItems="center"
            color={colors.lightContentGrey}
            bg={colors.selectionGrey}
            border={`1px solid ${colors.elementGrey}`}
            borderRight="0"
            borderRadius="8px 0 0 8px"
            transition={`all ${transition}`}
          >
            <BodySmall styleAs="h5">{prefix}</BodySmall>
          </Prefix>
        </Box>
      );
    }

    return (
      <>
        <input
          // @ts-expect-error TS(2322): Type 'ForwardedRef<unknown>' is not assignable to ... Remove this comment to see the full error message
          ref={ref}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...otherProps}
          onChange={onChange}
          id={id}
          className={className}
        />
        {hasSuccess && (
          <Box
            position="absolute"
            right="8px"
            top="50%"
            transform="translatey(-50%)"
            display="flex"
            width={24}
            height={24}
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            bg={colors.brandGreen}
          >
            <AnimatedCheckmark size={24} colorName="white" />
          </Box>
        )}
      </>
    );
  }),
)`
  ${(props) => (props.prefix ? 'input' : '&')} {
    margin: 0;
    padding: 11px 12px;
    font: inherit;
    font-size: 18px;
    color: ${colors.darkContentGrey};
    border-radius: ${(props) => (props.prefix ? '0 8px 8px 0' : '8px')};
    border: solid 1px;
    border-color: ${colors.elementGrey};
    height: 45px;
    width: 100%;
    background-color: ${colors.white};
    appearance: none;
    transition: all ${transition};
    ::placeholder {
      color: ${colors.lightContentGrey};
    }
    &:focus {
      outline: 0;
    }
    ${(props) =>
      props.isValid
        ? `
    border-color: ${colors.brandGreen};
    box-shadow: 0 0 0 1px ${colors.brandGreen};
        `
        : `
    &:focus {
      border-color: ${colors.brandBlue};
      box-shadow: 0 0 0 1px ${colors.brandBlue};
      & + ${Prefix} {
        background: ${colors.brandBlue};
        box-shadow: 0 0 0 1px ${colors.brandBlue};
        border-color: ${colors.brandBlue};
        color: white;
      }
    }`}
    ${(props) =>
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      props.hasError
        ? `
      border-color: ${colors.alertRed};
      box-shadow: 0 0 0 1px ${colors.alertRed};
    `
        : props.hasSuccess
        ? `
      border-color: ${colors.brandGreen};
      box-shadow: 0 0 0 1px ${colors.brandGreen};
    `
        : ''}
    &:disabled {
      background-color: ${colors.inputGrey};
    }
  }
`;
